import { useMemo, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { useTourGuide, TourGuideConfig } from "./useTourGuide";
import { useTourGuideContext } from "@root/context/tour-guide-context";
import { useTourGuideHover } from "./useTourGuideHover";
import { useAutoTourGuide } from "./useAutoTourGuide";
import { ROUTE_NAMES } from "@root/constants";

export const useMaterialComparisonTourGuide = () => {
  const { t } = useTranslation("common");
  const { pathname } = useLocation();
  const { setHighlightedItemKey, setActiveTourModule, hoverTooltipsEnabled } =
    useTourGuideContext();

  // Tour guide refs
  const filesListContainerRef = useRef<HTMLDivElement>(null);
  const startAnalysisRef = useRef<HTMLDivElement>(null);

  const isMaterialComparison = pathname === ROUTE_NAMES.MATERIAL_CAMPARISON;

  // Tour guide configuration for material comparison
  const materialComparisonTourConfig: TourGuideConfig = useMemo(() => {
    if (!isMaterialComparison) {
      return {
        moduleId: "materialComparison",
        steps: [],
        enabled: false,
      };
    }

    return {
      moduleId: "materialComparison",
      enabled: true,
      steps: [
        {
          id: "sidebar-material",
          targetSelector: '[data-tour-item="materialComparisonNested"]',
          title:
            t("tourGuide.materialComparison.step1.title") ||
            "Material Comparison System",
          description:
            t("tourGuide.materialComparison.step1.description") ||
            "Welcome to Material Comparison! This powerful tool compares contracts and drawings side-by-side to identify discrepancies and inconsistencies.",
          position: "right" as const,
          offset: { x: 20, y: 0 },
        },
        {
          id: "contract-upload",
          targetSelector: '[data-tour-item="material-contract-upload"]',
          title:
            t("tourGuide.materialComparison.step2.title") || "Upload Contracts",
          description:
            t("tourGuide.materialComparison.step2.description") ||
            "Drag and drop contract files here. Supported formats: PDF up to 50MB. You can upload multiple contracts to compare.",
          position: "bottom" as const,
          offset: { x: 0, y: 12 },
        },
        {
          id: "drawing-upload",
          targetSelector: '[data-tour-item="material-drawing-upload"]',
          title:
            t("tourGuide.materialComparison.step3.title") || "Upload Drawings",
          description:
            t("tourGuide.materialComparison.step3.description") ||
            "Drag and drop drawing files here. Upload blueprints, technical drawings, or specifications to compare against your contracts.",
          position: "bottom" as const,
          offset: { x: 0, y: 12 },
        },
        {
          id: "files-list",
          targetRef: filesListContainerRef,
          title:
            t("tourGuide.materialComparison.step4.title") ||
            "File Status & Management",
          description:
            t("tourGuide.materialComparison.step4.description") ||
            "Track upload status (Uploaded, In Progress, Failed) and manage files. Select files to compare by clicking the checkboxes.",
          position: "top" as const,
          offset: { x: 0, y: -12 },
        },
        {
          id: "start-analysis",
          targetRef: startAnalysisRef,
          title:
            t("tourGuide.materialComparison.step5.title") || "Start Comparison",
          description:
            t("tourGuide.materialComparison.step5.description") ||
            'Once files are uploaded, click "Start Analysis" to begin the AI-powered comparison. View results in "View History".',
          position: "bottom" as const,
          offset: { x: 0, y: 12 },
        },
      ],
    };
  }, [isMaterialComparison, t]);

  const baseTourGuide = useTourGuide(materialComparisonTourConfig);
  
  // Auto-start tour guide and handle completion tracking
  const materialComparisonTourGuide = useAutoTourGuide({
    tourGuide: baseTourGuide,
    tourCompletionFlag: "material_comparison_tour_completed",
    isOnTourPath: isMaterialComparison,
  });

  // Sync tour guide with context
  useEffect(() => {
    if (materialComparisonTourGuide.isActive) {
      setActiveTourModule("materialComparison");
      if (materialComparisonTourGuide.currentStep === 0) {
        setHighlightedItemKey("materialComparisonNested");
      } else {
        setHighlightedItemKey(null);
      }
    } else {
      setActiveTourModule(null);
      setHighlightedItemKey(null);
    }
  }, [
    materialComparisonTourGuide.isActive,
    materialComparisonTourGuide.currentStep,
    setActiveTourModule,
    setHighlightedItemKey,
  ]);

  // Hover tooltips for all steps
  const sidebarMaterialStep = materialComparisonTourConfig.steps.find(
    (step) => step.id === "sidebar-material"
  );
  const contractUploadStep = materialComparisonTourConfig.steps.find(
    (step) => step.id === "contract-upload"
  );
  const drawingUploadStep = materialComparisonTourConfig.steps.find(
    (step) => step.id === "drawing-upload"
  );
  const filesListStep = materialComparisonTourConfig.steps.find(
    (step) => step.id === "files-list"
  );
  const startAnalysisStep = materialComparisonTourConfig.steps.find(
    (step) => step.id === "start-analysis"
  );

  const sidebarMaterialHover = useTourGuideHover(
    "sidebar-material",
    sidebarMaterialStep || null
  );
  const contractUploadHover = useTourGuideHover(
    "contract-upload",
    contractUploadStep || null
  );
  const drawingUploadHover = useTourGuideHover(
    "drawing-upload",
    drawingUploadStep || null
  );
  const filesListHover = useTourGuideHover("files-list", filesListStep || null);
  const startAnalysisHover = useTourGuideHover(
    "start-analysis",
    startAnalysisStep || null
  );

  // Attach hover handlers to contract upload via selector
  useEffect(() => {
    if (
      !hoverTooltipsEnabled ||
      materialComparisonTourGuide.isActive ||
      !contractUploadStep
    )
      return;

    const element = document.querySelector(
      '[data-tour-item="material-contract-upload"]'
    ) as HTMLElement;
    if (element) {
      const handleMouseEnter = () => {
        if (!contractUploadHover.tooltipData) return;
        contractUploadHover.hoverProps.onMouseEnter();
      };
      const handleMouseLeave = () => {
        contractUploadHover.hoverProps.onMouseLeave();
      };

      element.addEventListener("mouseenter", handleMouseEnter);
      element.addEventListener("mouseleave", handleMouseLeave);

      return () => {
        element.removeEventListener("mouseenter", handleMouseEnter);
        element.removeEventListener("mouseleave", handleMouseLeave);
      };
    }
  }, [
    hoverTooltipsEnabled,
    materialComparisonTourGuide.isActive,
    contractUploadStep,
    contractUploadHover.hoverProps,
    contractUploadHover.tooltipData,
  ]);

  // Attach hover handlers to drawing upload via selector
  useEffect(() => {
    if (
      !hoverTooltipsEnabled ||
      materialComparisonTourGuide.isActive ||
      !drawingUploadStep
    )
      return;

    const element = document.querySelector(
      '[data-tour-item="material-drawing-upload"]'
    ) as HTMLElement;
    if (element) {
      const handleMouseEnter = () => {
        if (!drawingUploadHover.tooltipData) return;
        drawingUploadHover.hoverProps.onMouseEnter();
      };
      const handleMouseLeave = () => {
        drawingUploadHover.hoverProps.onMouseLeave();
      };

      element.addEventListener("mouseenter", handleMouseEnter);
      element.addEventListener("mouseleave", handleMouseLeave);

      return () => {
        element.removeEventListener("mouseenter", handleMouseEnter);
        element.removeEventListener("mouseleave", handleMouseLeave);
      };
    }
  }, [
    hoverTooltipsEnabled,
    materialComparisonTourGuide.isActive,
    drawingUploadStep,
    drawingUploadHover.hoverProps,
    drawingUploadHover.tooltipData,
  ]);

  return {
    filesListContainerRef,
    startAnalysisRef,
    materialComparisonTourGuide,
    materialComparisonTourConfig,
    isMaterialComparison,
    sidebarMaterialHover,
    contractUploadHover,
    drawingUploadHover,
    filesListHover,
    startAnalysisHover,
  };
};
